/**
 * SaasEventsTable — §2 "Linear/Vercel professional tool" events list renderer.
 *
 * Hairline divide-y list: 40px thumbs, text-[13px] sans, hover:bg-muted rows,
 * right-aligned quick-action icons appearing on group hover (opacity-0 →
 * opacity-100). Open icon + download icon reuse the page's existing callbacks.
 * Selection checkbox in left cell when selectionMode active.
 *
 * Props: plain data + callbacks — no hooks, no queries.
 */

import { useState } from 'react'
import { Download, Trash2, ExternalLink, Play, Image } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { DetectionChip } from '@/components/ui/detection-chip'
import { getEventDownloads, downloadFile } from '@/lib/eventDownload'
import { cn, formatRelativeTime } from '@/lib/utils'
import type { Event } from '@/types'

// ── sub-components ────────────────────────────────────────────────────────────

function SaasThumb({ event }: { event: Event }) {
  const [imgError, setImgError] = useState(false)
  const mediaType = event.media_details?.media_type ?? 'image'
  return (
    <div className="relative h-[40px] w-[40px] flex-shrink-0 overflow-hidden rounded bg-muted">
      {event.thumbnail_url && !imgError ? (
        <img
          src={event.thumbnail_url}
          alt={event.event_type}
          className="h-full w-full object-cover"
          onError={() => setImgError(true)}
        />
      ) : (
        <div className="flex h-full w-full items-center justify-center">
          {mediaType === 'video' ? (
            <Play className="h-3 w-3 text-muted-foreground/50" />
          ) : (
            <Image className="h-3 w-3 text-muted-foreground/50" />
          )}
        </div>
      )}
    </div>
  )
}

function SelectCell({ selected }: { selected: boolean }) {
  return (
    <div
      className={cn(
        'h-4 w-4 rounded border flex items-center justify-center flex-shrink-0',
        selected ? 'bg-primary border-primary' : 'border-muted-foreground/40',
      )}
    >
      {selected && (
        <svg viewBox="0 0 8 8" className="h-2.5 w-2.5 fill-primary-foreground">
          <path d="M1 4l2 2 4-4" stroke="currentColor" strokeWidth="1.5" fill="none" />
        </svg>
      )}
    </div>
  )
}

// ── main component ────────────────────────────────────────────────────────────

export interface SaasEventsTableHandlers {
  onEventClick: (index: number) => void
  onToggleSelect: (id: number) => void
  onDeleteRequest: (event: Event) => void
}

export interface SaasEventsTableProps extends SaasEventsTableHandlers {
  events: Event[]
  selectionMode: boolean
  selectedIds: Set<number>
}

export function SaasEventsTable({
  events,
  selectionMode,
  selectedIds,
  onEventClick,
  onToggleSelect,
  onDeleteRequest,
}: SaasEventsTableProps) {
  return (
    <div className="overflow-x-auto rounded border border-border divide-y divide-border" data-testid="saas-events-table">
      {events.map((event, index) => (
        <SaasRow
          key={event.id}
          event={event}
          index={index}
          selectionMode={selectionMode}
          selected={selectedIds.has(event.id)}
          onEventClick={onEventClick}
          onToggleSelect={onToggleSelect}
          onDeleteRequest={onDeleteRequest}
        />
      ))}
    </div>
  )
}

// ── row sub-component (calls useTranslation directly) ─────────────────────────

interface SaasRowProps {
  event: Event
  index: number
  selectionMode: boolean
  selected: boolean
  onEventClick: (i: number) => void
  onToggleSelect: (id: number) => void
  onDeleteRequest: (e: Event) => void
}

function SaasRow({
  event,
  index,
  selectionMode,
  selected,
  onEventClick,
  onToggleSelect,
  onDeleteRequest,
}: SaasRowProps) {
  const { t } = useTranslation('events')
  const { t: tc } = useTranslation('common')
  const { annotated } = getEventDownloads(event)
  const eventTypeDisplay = tc(`eventTypes.${event.event_type}`, { defaultValue: event.event_type.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()) })

  const handleRowClick = () => {
    if (selectionMode) {
      onToggleSelect(event.id)
    } else {
      onEventClick(index)
    }
  }

  return (
    <div
      className={cn(
        'group flex items-center gap-3 px-2 py-2 text-[13px] cursor-pointer transition-colors duration-[120ms]',
        'hover:bg-muted',
        selected && 'bg-primary/5',
      )}
      onClick={handleRowClick}
      data-testid="saas-row"
    >
      {/* Selection checkbox */}
      {selectionMode && (
        <div
          className="flex-shrink-0"
          onClick={(e) => { e.stopPropagation(); onToggleSelect(event.id) }}
        >
          <SelectCell selected={selected} />
        </div>
      )}

      {/* Thumbnail */}
      <SaasThumb event={event} />

      {/* Timestamp */}
      <div className="w-32 flex-shrink-0 tabular-nums text-muted-foreground text-[12px]">
        {formatRelativeTime(event.occurred_at)}
      </div>

      {/* Camera */}
      <div className="w-36 flex-shrink-0 font-medium truncate">
        {event.camera_name}
      </div>

      {/* Detection chips */}
      <div className="flex flex-1 flex-wrap gap-1 min-w-0">
        {event.detected_objects.slice(0, 3).map((cls) => (
          <DetectionChip key={cls} cls={cls} type="object" />
        ))}
        {event.audio_detections.slice(0, 2).map((d) => (
          <DetectionChip key={d.class_name} cls={d.class_name} confidence={d.confidence} type="audio" />
        ))}
        {!event.detected_objects.length && !event.audio_detections.length && (
          <span className="text-muted-foreground/50">{eventTypeDisplay}</span>
        )}
      </div>

      {/* Right-aligned quick actions — fade in on row hover */}
      {!selectionMode && (
        <div
          className="flex items-center gap-1 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity duration-[120ms]"
          onClick={(e) => e.stopPropagation()}
        >
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 text-muted-foreground hover:text-foreground"
            onClick={() => onEventClick(index)}
            title={t('card.open', { defaultValue: 'Open' })}
          >
            <ExternalLink className="h-3.5 w-3.5" />
            <span className="sr-only">{t('card.open', { defaultValue: 'Open' })}</span>
          </Button>
          {annotated && (
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 text-muted-foreground hover:text-foreground"
              onClick={() => downloadFile(annotated.url, annotated.filename)}
              title={t('card.download')}
            >
              <Download className="h-3.5 w-3.5" />
              <span className="sr-only">{t('card.download')}</span>
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 text-muted-foreground hover:text-destructive"
            onClick={() => onDeleteRequest(event)}
            title={t('card.deleteEvent')}
          >
            <Trash2 className="h-3.5 w-3.5" />
            <span className="sr-only">{t('card.deleteEvent')}</span>
          </Button>
        </div>
      )}
    </div>
  )
}
